import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import masterService from "../../services/masterService";
import authService from "../../services/authService";
import { useAuth } from "../../context/AuthContext";
import RecommendedUsers from "./RecommendedUsers";

const GuestSearch = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  // Search form state
  const [searchForm, setSearchForm] = useState({
    age_min: "",
    age_max: "",
    location: "",
    qualification: "",
    gender: "",
    country: "",
    state: "",
    city: "",
    caste: "",
    caste_category: "",
    raasi: "",
    nakshatra: "",
    marital_status: "",
    religion: "",
    occupation: "",
    income: "",
    padam: "",
    height_cm: "",
    father_occupation: "",
    mother_occupation: "",
    brother_count: "",
    sister_count: "",
    complexion: "",
  });

  // Results and loading state
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  // Subscription details state
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Dropdown data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [raasis, setRaasis] = useState([]);
  const [nakshatras, setNakshatras] = useState([]);
  const [castes, setCastes] = useState([]);
  const [casteCategories, setCasteCategories] = useState([]);
  const [padams, setPadams] = useState([]);

  // Static options for fields that don't require API calls
  const maritalStatusOptions = [
    { id: "Single", name: "Single" },
    { id: "Married", name: "Married" },
    { id: "Divorced", name: "Divorced" },
    { id: "widowed", name: "Widowed" },
  ];

  const religionOptions = [
    { id: "hindu", name: "Hindu" },
    { id: "muslim", name: "Muslim" },
    { id: "christian", name: "Christian" },
    { id: "sikh", name: "Sikh" },
    { id: "buddhist", name: "Buddhist" },
    { id: "jain", name: "Jain" },
    { id: "other", name: "Other" },
  ];

  const incomeOptions = [
    { id: "< 10L", name: "< 10L" },
    { id: "10-30L", name: "10-30L" },
    { id: "30-50L", name: "30-50L" },
    { id: "> 50L", name: "> 50L" },
  ];

  const heightOptions = [
    { id: "5ft 2in - 157cm", name: "5ft 2in - 157cm" },
    { id: "5ft 3in - 160cm", name: "5ft 3in - 160cm" },
    { id: "5ft 4in - 162cm", name: "5ft 4in - 162cm" },
    { id: "5ft 5in - 165cm", name: "5ft 5in - 165cm" },
    { id: "5ft 6in - 167cm", name: "5ft 6in - 167cm" },
    { id: "5ft 7in - 170cm", name: "5ft 7in - 170cm" },
    { id: "5ft 8in - 172cm", name: "5ft 8in - 172cm" },
    { id: "5ft 9in - 175cm", name: "5ft 9in - 175cm" },
    { id: "5ft 10in - 177cm", name: "5ft 10in - 177cm" },
    { id: "5ft 11in - 180cm", name: "5ft 11in - 180cm" },
    { id: "6ft - 182cm", name: "6ft - 182cm" },
    { id: "6ft 1in - 185cm", name: "6ft 1in - 185cm" },
    { id: "6ft 2in - 187cm", name: "6ft 2in - 187cm" },
  ];

  // Local options for fields that don't require API calls
  const preferredCallTimeOptions = [
    { id: "8 AM - 12 PM", name: "8 AM - 12 PM" },
    { id: "12 PM - 4 PM", name: "12 PM - 4 PM" },
    { id: "4 PM - 8 PM", name: "4 PM - 8 PM" },
    { id: "8PM - 10 PM", name: "8 PM - 10 PM" },
    { id: "Any Time", name: "Any Time" },
  ];

  const complexionOptions = [
    { id: "Very Fair", name: "Very Fair" },
    { id: "Fair", name: "Fair" },
    { id: "Wheatish", name: "Wheatish" },
    { id: "Wheatish Brown", name: "Wheatish Brown" },
    { id: "Dusky", name: "Dusky" },
    { id: "Dark", name: "Dark" },
  ];

  const occupationOptions = [
    { id: "Business", name: "Business" },
    { id: "Govt Employee", name: "Govt Employee" },
    { id: "Private Employee", name: "Private Employee" },
    { id: "Other", name: "Other" },
    { id: "Home Maker", name: "Home Maker" },
    { id: "Expired", name: "Expired" },
  ];

  // Fetch subscription details
  const fetchSubscriptionDetails = async () => {
    if (!user || !user.id) {
      return;
    }
    
    setSubscriptionLoading(true);
    try {
      const details = await authService.getUserSubscriptionDetails();
      
      // Check if the response has the expected structure
      if (details && (details.subscription_details || details.user)) {
        setSubscriptionDetails(details);
      } else {
        setSubscriptionDetails(null);
      }
      } catch (error) {
        setSubscriptionDetails(null);
      } finally {
      setSubscriptionLoading(false);
    }
  };

  // Determine user's field access level
  const getUserFieldAccess = () => {
    // If user is not logged in
    if (!user) {
      return 'guest';
    }
    
    // If subscription is still loading, treat as guest
    if (subscriptionLoading) {
      return 'guest';
    }
    
    // If no subscription details available
    if (!subscriptionDetails || !subscriptionDetails.subscription_details) {
      return 'guest';
    }
    
    // Check plan_id for limited access
    const planId = subscriptionDetails.subscription_details.plan_id;
    
    if (planId == 1 || planId == 4) {
      return 'limited';
    }
    
    // All other plans have full access
    return 'full';
  };

  // Check if user has any subscription (similar to Dashboard)
  const hasSubscription = () => {
    if (!user || subscriptionLoading) {
      return false;
    }
    
    if (!subscriptionDetails || !subscriptionDetails.subscription_details) {
      return false;
    }
    
    // Check if user has any active subscription
    const planId = subscriptionDetails.subscription_details.plan_id;
    return planId && planId > 0;
  };
  // Load dropdown data and subscription details on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          countriesData,
          qualificationsData,
          raasisData,
          nakshatrasData,
          castesData,
          casteCategoriesData,
        ] = await Promise.all([
          masterService.getCountryList(),
          masterService.getQualificationList(),
          masterService.getRaasiList(),
          masterService.getNakshatraList(),
          masterService.getCasteList(),
          masterService.getCasteCategoryList(),
        ]);

        setCountries(countriesData || []);
        setQualifications(qualificationsData || []);
        setRaasis(raasisData || []);
        setNakshatras(nakshatrasData || []);
        setCastes(castesData || []);
        setCasteCategories(casteCategoriesData || []);
      } catch (error) {
        setError("Failed to load data");
      }
    };

    loadData();
    fetchSubscriptionDetails();
  }, [user]);

  // Handle form changes
  const handleFormChange = async (e) => {
    const { name, value } = e.target;
    setSearchForm((prev) => ({ ...prev, [name]: value }));

    // Handle dependent dropdowns
    if (name === "country") {
      setSearchForm((prev) => ({ ...prev, state: "", city: "" }));
      setStates([]);
      setCities([]);
      if (value) {
        try {
          const statesData = await masterService.getStatesList(value);
          setStates(statesData || []);
        } catch (error) {
          // Error loading states
        }
      }
    }

    if (name === "state") {
      setSearchForm((prev) => ({ ...prev, city: "" }));
      setCities([]);
      if (value) {
        try {
          const citiesData = await masterService.getCitiesList(value);
          setCities(citiesData || []);
        } catch (error) {
          // Error loading cities
        }
      }
    }

    if (name === "nakshatra") {
      setSearchForm((prev) => ({ ...prev, padam: "" }));
      setPadams([]);
      if (value) {
        try {
          const padamsData = await masterService.getPadamList(value);
          setPadams(padamsData || []);
        } catch (error) {
          // Error loading padams
        }
      }
    }
  };

  // Clear all form fields
  const handleClearAll = () => {
    setSearchForm({
      age_min: "",
      age_max: "",
      location: "",
      qualification: "",
      gender: "",
      country: "",
      state: "",
      city: "",
      caste: "",
      caste_category: "",
      raasi: "",
      nakshatra: "",
      marital_status: "",
      religion: "",
      occupation: "",
      income: "",
      padam: "",
      height_cm: "",
      father_occupation: "",
      mother_occupation: "",
      brother_count: "",
      sister_count: "",
      complexion: "",
    });
    setSearchResults([]);
    setSearched(false);
    setError("");
    setStates([]);
    setCities([]);
    setPadams([]);
  };

  // Handle search
  const handleSearch = async () => {
    // Check if at least one field is selected
    const hasAnyValue = Object.values(searchForm).some(value => value !== "");
    
    if (!hasAnyValue) {
      // Clear previous results when validation fails
      setSearchResults([]);
      setSearched(false);
      setError("Please select at least one search criteria (like age, location, education, etc.) to find your perfect match.");
      return;
    }

    // Clear previous results and error before starting new search
    setSearchResults([]);
    setError("");
    setSearched(true);
    setLoading(true);

    try {
      // Filter out empty values
      const searchParams = Object.fromEntries(
        Object.entries(searchForm).filter(([_, value]) => value !== "")
      );

      const results = await masterService.searchUsers(searchParams);
      setSearchResults(results || []);
    } catch (error) {
      setError("Failed to search users. Please try again.");
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Like handler with backend call
  const handleHeartClick = async (profileId) => {
    if (!user || !user.id) {
      navigate('/login-page');
      return;
    }

    const profile = searchResults.find((p) => p.id === profileId);
    const isLiked = profile?.is_liked_by_me;
    const action = isLiked ? "unliked" : "liked";

    try {
      await masterService.likeUser(profileId, user.id, action);

      // Update state in both searchResults and recommendedUsers
      setSearchResults((prevResults) =>
        prevResults.map((profile) =>
          profile.id === profileId
            ? {
                ...profile,
                is_liked_by_me: !profile.is_liked_by_me,
                liked_count: profile.is_liked_by_me
                  ? (profile.liked_count || 1) - 1
                  : (profile.liked_count || 0) + 1,
              }
            : profile
        )
      );
    } catch (error) {
      alert("Failed to update like. Please try again.");
    }
  };

  // Interest handler with backend call
  const handleInterested = async (profileId) => {
    if (!user || !user.id) {
      navigate('/login-page');
      return;
    }

    const profile = searchResults.find((p) => p.id === profileId);
    const isInterested = profile?.is_interested_by_me;
    const action = isInterested ? "not_interested" : "interested";

    try {
      await masterService.interestUser(profileId, user.id, action);

      // Update state in both searchResults and recommendedUsers
      setSearchResults((prevResults) =>
        prevResults.map((profile) =>
          profile.id === profileId
            ? {
                ...profile,
                is_interested_by_me: !profile.is_interested_by_me,
              }
            : profile
        )
      );
    } catch (error) {
      alert("Failed to update interest. Please try again.");
    }
  };

  // Field styling to match Profile.jsx
  const fieldSx = {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.98)",
    minWidth: 280,
    maxWidth: 380,
    fontSize: "0.95rem",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      "&:hover": { 
        boxShadow: "0 6px 25px rgba(0,0,0,0.12)",
        transform: "translateY(-2px)",
      },
      "&:hover fieldset": { 
        borderColor: theme.palette.primary.light,
        borderWidth: "2px",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: "2px",
        boxShadow: `0 0 0 4px ${theme.palette.primary.main}1a`,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
      fontWeight: 500,
      "&.Mui-focused": { 
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
    },
    "& .MuiAutocomplete-root": {
      borderRadius: "16px",
    },
  };

  // Render select field helper using Autocomplete
  const renderSelectField = (name, label, options, gridMd = 4) => {
    // Ensure options are valid and properly formatted
    const validOptions = (options || []).filter(option => {
      if (!option) return false;
      if (typeof option === 'string' || typeof option === 'number') return true;
      if (typeof option === 'object') {
        return option.id || option.value || option.name;
      }
      return false;
    });

    return (
      <Grid item xs={12} md={gridMd}>
        <Autocomplete
          options={validOptions}
          getOptionLabel={(option) => {
            // Ensure we always return a string
            if (typeof option === 'string') return option;
            if (typeof option === 'number') return String(option);
            if (option && typeof option === 'object') {
              const label = option.name || option.value || option.id || '';
              return String(label);
            }
            return '';
          }}
          value={validOptions.find(option => {
            // More flexible matching for different data structures
            const optionId = option.id || option.value;
            const optionName = option.name || option.value;
            const formValue = searchForm[name];
            
            return optionId === formValue || 
                   optionName === formValue || 
                   String(optionId) === String(formValue) ||
                   String(optionName) === String(formValue);
          }) || null}
          onChange={(event, newValue) => {
            handleFormChange({
              target: {
                name,
                value: newValue ? newValue.id || newValue.value || newValue : "",
              },
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              sx={fieldSx}
              size="medium"
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {option.name || option.value || option}
                </Typography>
              </Box>
            </Box>
          )}
          filterOptions={(options, { inputValue }) => {
            const filterValue = inputValue.toLowerCase();
            return options.filter(option => {
              const optionText = String(option.name || option.value || option).toLowerCase();
              return optionText.includes(filterValue);
            });
          }}
          sx={{
            "& .MuiAutocomplete-inputRoot": {
              borderRadius: "16px",
            },
          }}
        />
      </Grid>
    );
  };

  const handlePurchase = async (receiverId) => {
    if (!user || !user.id) {
      navigate('/login-page');
      return;
    }

    try {
      const payload = {
        purpose: "Plan Subscription single profile",
        amount: 200,
        name: user.first_name + " " + user.last_name,
        email: user.email,
        phone: user.mobile,
        user_unique_id: user.unique_id,
        purchased_user_id: receiverId,
        redirect_type: "web",
      };

      const response = await masterService.createPayment(payload);
      if (response.status === "success") {
        window.location.href = response.payment_url; // redirect to Instamojo
      } else {
        alert("Failed to create payment.");
      }
    } catch (error) {
      alert("Error creating payment.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
        background: `radial-gradient(circle at 0% 0%, ${theme.palette.primary.light}11 0%, transparent 60%)`,
        py: { xs: 4, md: 8 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Navigation Buttons */}
      <Box
        sx={{
          position: "absolute",
          top: 20,
          left: 20,
          zIndex: 10,
          display: "flex",
          gap: 2,
        }}
      >
        <IconButton
          onClick={() => navigate("/")}
          sx={{
            bgcolor: "rgba(255,255,255,0.95)",
            "&:hover": {
              bgcolor: theme.palette.primary.light,
              transform: "scale(1.1)",
            },
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            transition: "all 0.3s",
          }}
        >
          <HomeIcon sx={{ color: theme.palette.primary.main }} />
        </IconButton>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: "rgba(255,255,255,0.95)",
            "&:hover": {
              bgcolor: theme.palette.primary.light,
              transform: "scale(1.1)",
            },
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            transition: "all 0.3s",
          }}
        >
          <ArrowBackIcon sx={{ color: theme.palette.primary.main }} />
        </IconButton>
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 800,
            color: theme.palette.primary.main,
            textAlign: "center",
            letterSpacing: 1,
          }}
        >
         Discover Your Perfect Match
        </Typography>

        <Card
          elevation={4}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: "18px",
            background: `linear-gradient(135deg, ${theme.palette.primary.light}11 0%, ${theme.palette.primary.main}08 100%)`,
            boxShadow: "0 4px 24px rgba(62,2,23,0.10)",
            mb: 5,
          }}
        >
          <Grid container spacing={3}>
            {(() => {
              const fieldAccess = getUserFieldAccess();
              
              switch (fieldAccess) {
                case 'guest':
                  return (
                    <>
                      {/* Guest/No subscription - 7 fields only */}
                      {renderSelectField("marital_status", "Marital Status", maritalStatusOptions, 4)}
                      {renderSelectField("gender", "Gender", [{ id: "male", name: "Male" }, { id: "female", name: "Female" }], 4)}
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Min Age"
                          name="age_min"
                          type="number"
                          value={searchForm.age_min}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Max Age"
                          name="age_max"
                          type="number"
                          value={searchForm.age_max}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={fieldSx}
                        />
                      </Grid>
                      {renderSelectField("raasi", "Raasi", raasis, 4)}
                      {renderSelectField("nakshatra", "Nakshatra", nakshatras, 4)}
                      {renderSelectField("padam", "Padam", padams, 4)}
                      {renderSelectField("caste", "Caste/Community", castes, 4)}
                    </>
                  );

                case 'limited':
                  return (
                    <>
                      {/* Limited subscription (plan_id 1 or 4) - 12 fields */}
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Min Age"
                          name="age_min"
                          type="number"
                          value={searchForm.age_min}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Max Age"
                          name="age_max"
                          type="number"
                          value={searchForm.age_max}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={fieldSx}
                        />
                      </Grid>
                      {renderSelectField("gender", "Gender", [{ id: "male", name: "Male" }, { id: "female", name: "Female" }], 3)}
                      {renderSelectField("marital_status", "Marital Status", maritalStatusOptions, 3)}
                      {renderSelectField("religion", "Religion", religionOptions, 3)}
                      {renderSelectField("caste", "Caste/Community", castes, 4)}
                      {renderSelectField("country", "Country", countries, 4)}
                      {renderSelectField("state", "State", states, 4)}
                      {renderSelectField("city", "City", cities, 4)}
                      {renderSelectField("qualification", "Education", qualifications, 4)}
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Occupation" name="occupation" value={searchForm.occupation} onChange={handleFormChange} variant="outlined" sx={fieldSx} />
                      </Grid>
                      {renderSelectField("income", "Annual Income", incomeOptions, 4)}
                      {renderSelectField("raasi", "Raasi", raasis, 4)}
                      {renderSelectField("nakshatra", "Nakshatra", nakshatras, 4)}
                      {renderSelectField("padam", "Padam", padams, 4)}
                    </>
                  );

                case 'full':
                default:
                  return (
                    <>
                      {/* Full access - All fields */}
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Min Age"
                          name="age_min"
                          type="number"
                          value={searchForm.age_min}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Max Age"
                          name="age_max"
                          type="number"
                          value={searchForm.age_max}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={fieldSx}
                        />
                      </Grid>
                      {renderSelectField("gender", "Gender", [{ id: "male", name: "Male" }, { id: "female", name: "Female" }], 3)}
                      {renderSelectField("marital_status", "Marital Status", maritalStatusOptions, 3)}
                      {renderSelectField("religion", "Religion", religionOptions, 3)}
                      {renderSelectField("caste", "Caste/Community", castes, 4)}
                      {renderSelectField("country", "Country", countries, 4)}
                      {renderSelectField("state", "State", states, 4)}
                      {renderSelectField("city", "City", cities, 4)}
                      {renderSelectField("qualification", "Education", qualifications, 4)}
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Occupation" name="occupation" value={searchForm.occupation} onChange={handleFormChange} variant="outlined" sx={fieldSx} />
                      </Grid>
                      {renderSelectField("income", "Annual Income", incomeOptions, 4)}
                      {renderSelectField("raasi", "Raasi", raasis, 4)}
                      {renderSelectField("nakshatra", "Nakshatra", nakshatras, 4)}
                      {renderSelectField("padam", "Padam", padams, 4)}
                      {/* Additional fields for full access */}
                      {renderSelectField("caste_category", "Caste Category", casteCategories, 4)}
                      {renderSelectField("height_cm", "Height", heightOptions, 4)}
                      {renderSelectField("complexion", "Complexion", complexionOptions, 4)}
                      {renderSelectField("father_occupation", "Father's Occupation", occupationOptions, 4)}
                      {renderSelectField("mother_occupation", "Mother's Occupation", occupationOptions, 4)}
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Brothers Count" name="brother_count" type="number" value={searchForm.brother_count} onChange={handleFormChange} variant="outlined" sx={fieldSx} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Sisters Count" name="sister_count" type="number" value={searchForm.sister_count} onChange={handleFormChange} variant="outlined" sx={fieldSx} />
                      </Grid>
                    </>
                  );
              }
            })()}
          </Grid>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleClearAll}
              disabled={loading}
              sx={{
                px: 4,
                py: 1.5,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.primary.light + "20",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 15px rgba(62,2,23,0.12)",
                },
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "12px",
              }}
            >
              Clear All
            </Button>
            
            <Button
              variant="contained"
              size="large"
              onClick={handleSearch}
              disabled={loading}
              sx={{
                px: 4,
                py: 1.5,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                "&:hover": {
                  background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.light})`,
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 15px rgba(62,2,23,0.18)",
                },
                fontSize: "1.1rem",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "12px",
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Search"
              )}
            </Button>
          </Box>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 3,
              fontSize: '1.1rem',
              fontWeight: 600,
              boxShadow: '0 4px 20px rgba(211, 47, 47, 0.15)',
              border: '2px solid #d32f2f',
              background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
              '& .MuiAlert-icon': {
                fontSize: '1.5rem',
                color: '#d32f2f'
              },
              '& .MuiAlert-message': {
                color: '#b71c1c',
                fontWeight: 600,
                textAlign: 'center',
                width: '100%'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Search Results Section */}
        {searched && (
          <RecommendedUsers
            users={searchResults}
            loading={loading}
            onHeartClick={handleHeartClick}
            onInterested={handleInterested}
            onPurchase={handlePurchase}
            theme={theme}
            hasSubscription={hasSubscription()}
            isLoggedIn={!!user}
            title={`Search Results (${searchResults.length})`}
          />
        )}
      </Container>
    </Box>
  );
};

export default GuestSearch;
